import React, { useMemo, useState, useEffect } from 'react'
import { TabContext } from '@mui/lab'
import { Box, List } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useFeatureFlag } from '../../../utils/hooks/useFeatureFlag'
import { isCemFeatureEnabled } from '../../../rsmCoreComponents/utils/featureFlagUtils'
import { getCemFeatures } from '../../../store/userInfo/userInfoSlice'
import { Styles } from '../../../types'
import {
  CEM_FEATURE_PAYMENT_PAY_INVOICES,
  CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
  CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
  CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
  CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
  CEM_FEATURE_MANAGE_ADMIN_SETTINGS,
} from '../../../utils/constants/constants'
import RouterNavListItem from '../../Navigation/NavListItem/RouterNavListItem'
import DropdownNavListItem from '../../Navigation/NavListItem/DropDownNavListItem'
import tokens from '../../../styles/tokens.json'
import { getUserReportApps } from '../../../store/userApps/userAppsSlice'

const styles: Styles = {
  nav: (theme) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    height: '100%',
    [theme.breakpoints.down('desktop')]: {
      display: 'none',
    },
  }),
  list: (theme) => ({
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    border: 'none',
    height: '100%',
    gap: '2.5rem',
    a: {
      '&[aria-current="true"]': {
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 'calc(100% - 0.125rem)',
          right: 0,
          bottom: 0,
          left: 0,
          width: '100%',
          height: '0.125rem',
          backgroundColor: theme.palette.secondary.main,
          zIndex: 1,
        },
      },
    },
  }),
  link: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1rem',
    textDecoration: 'none',
    color: tokens.colors.white,
    whiteSpace: 'nowrap',
    paddingLeft: '0rem',
    ':hover': {
      fontFamily: tokens.type.fontFamilies.preloBold,
    },
    '&[aria-current="true"]': {
      fontFamily: tokens.type.fontFamilies.preloBold,
    },
    '&[aria-current="true"]::after': {
      backgroundColor: `${tokens.colors.rsmBlue.primary} !important`, // important is needed to override theme styling
    },
    ':focus-visible': {
      outline: `.125rem solid ${tokens.colors.white}`,
      boxShadow: ` 0 0 0 .125rem ${tokens.colors.black}`,
    },
  },
}

type DesktopMenuProps = {
  currentItem?: string
  showWelcome?: boolean
}

const DesktopMenu = ({
  currentItem = '',
  showWelcome = false,
}: DesktopMenuProps) => {
  const { t } = useTranslation()
  const cemFeatures = useSelector(getCemFeatures)
  const isUIRedesignEnabled = useFeatureFlag('UI_Redesign')

  const invoicingEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [
          CEM_FEATURE_PAYMENT_PAY_INVOICES,
          CEM_FEATURE_PAYMENT_VIEW_INVOICES_AND_DOCUMENTS,
          CEM_FEATURE_PAYMENT_CLIENT_ADMIN,
        ],
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )
  const questionnairesEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [
          CEM_FEATURE_QUESTIONNAIRE_ASSIGN,
          CEM_FEATURE_QUESTIONNAIRE_EXTERNAL_COMPLETE,
        ],
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )

  const isAdminSettingsFlagEnabled = useFeatureFlag('AdminSettings')
  const adminSettingsEnabled = useMemo(
    () =>
      isCemFeatureEnabled(
        [CEM_FEATURE_MANAGE_ADMIN_SETTINGS],
        cemFeatures,
        'all',
      ) && isAdminSettingsFlagEnabled,
    [cemFeatures, isAdminSettingsFlagEnabled],
  )

  const engagementsEnabled = useFeatureFlag('Targeting_Engagement')
  const isBIReportingEnabled = useFeatureFlag('BI_Reporting')
  const isInvoicingEnabled = useFeatureFlag('Invoicing')
  const isQuestionnairesEnabled = useFeatureFlag('Questionnaires')
  const isSupportEnabled = useFeatureFlag('Support')
  const userApps = useSelector(getUserReportApps)
  const dropdownItems = [
    {
      label: t('Navigation.Support'),
      to: '/contact-us/support-page',
      testId: 'Lnk_Navigation_SupportDropdown',
    },
    {
      label: t('Navigation.Feedback'),
      to: '/contact-us/feedback',
      testId: 'Lnk_Navigation_FeedbackDropdown',
    },
    {
      label: t('Navigation.FAQ'),
      to: '/contact-us/faq',
      testId: 'Lnk_Navigation_FAQDropdown',
    },
  ]

  if (adminSettingsEnabled) {
    dropdownItems.push({
      label: t('Navigation.AdminSettings'),
      to: '/contact-us/admin-settings',
      testId: 'Lnk_Navigation_AdminSettingsDropdown',
    })
  }

  const [reportsMenuItems, setReportsMenuItems] = useState<
    { label: string; to: string; testId: string }[]
  >([])

  useEffect(() => {
    if (userApps.length > 0) {
      const items = userApps.map((r, idx) => {
        const id = (r.id ?? idx + 1).toString()
        const label = r.name?.trim()?.length ? r.name!.trim() : `Report ${id}`
        const launchUri = r.launchUri?.trim() || ''
        return {
          label,
          to: launchUri,
          testId: `Lnk_Navigation_Report_${id}`,
        }
      })
      setReportsMenuItems(items)
    } else {
      setReportsMenuItems([])
    }
  }, [isBIReportingEnabled, userApps])

  return (
    <Box component="nav" sx={styles.nav} aria-label={t('Navigation.Main')}>
      <TabContext value={currentItem}>
        <List sx={styles.list}>
          <RouterNavListItem
            to="/"
            value="home"
            testId="Lnk_Navigation_Home"
            linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
            {t('Navigation.Home')}
          </RouterNavListItem>

          {engagementsEnabled && (
            <RouterNavListItem
              to="/engagements"
              value="engagements"
              testId="Lnk_Navigation_Engagements"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.Engagements')}
            </RouterNavListItem>
          )}

          <RouterNavListItem
            to="/insights"
            value="insights"
            testId="Lnk_Navigation_Insights"
            linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
            {t('Navigation.Insights')}
          </RouterNavListItem>
          {isBIReportingEnabled && reportsMenuItems.length > 0 && (
            <DropdownNavListItem
              isCurrent={currentItem === 'report'}
              label={t('Navigation.Reports')}
              testId="Lnk_Navigation_Reports"
              linkStyle={
                isUIRedesignEnabled
                  ? {
                      ...styles.link,
                      borderBottom:
                        currentItem === 'report'
                          ? `0.125rem solid ${tokens.colors.rsmBlue.primary}`
                          : 'none',
                      paddingY: 0,
                      fontFamily:
                        currentItem === 'report' ? 'Prelo-Bold ' : 'Prelo-Book',
                      fontWeight: 400,
                      maxHeight: '1.625rem',
                    }
                  : undefined
              }
              items={
                reportsMenuItems.length > 0
                  ? reportsMenuItems
                  : [
                      {
                        label: t('Common.NoData') || 'No reports',
                        to: '#',
                        testId: 'Lnk_Navigation_Report_None',
                      },
                    ]
              }
              menuStyle={
                showWelcome
                  ? undefined
                  : {
                      backgroundColor: tokens.colors.black,
                      border: `0.0625rem solid ${tokens.colors.black}`,
                      '& a': {
                        color: tokens.colors.white,
                        '&:hover': {
                          backgroundColor: tokens.colors.white,
                          color: tokens.colors.black,
                        },
                      },
                    }
              }
            />
          )}
          {isInvoicingEnabled && invoicingEnabled && (
            <RouterNavListItem
              to="/invoicing"
              value="invoicing"
              testId="Lnk_Navigation_Invoicing"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.PayMyBill')}
            </RouterNavListItem>
          )}
          {isQuestionnairesEnabled && questionnairesEnabled && (
            <RouterNavListItem
              to="/questionnaires"
              value="questionnaires"
              testId="Lnk_Navigation_Questionnaires"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.Questionnaire.MyQuestionnaires')}
            </RouterNavListItem>
          )}
          {!isSupportEnabled && (
            <RouterNavListItem
              to="/support"
              value="support"
              testId="Lnk_Navigation_Support"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.Support')}
            </RouterNavListItem>
          )}
          {isSupportEnabled && isUIRedesignEnabled ? (
            <DropdownNavListItem
              isCurrent={currentItem === 'contact-us'}
              label={t('Navigation.ContactUs')}
              testId="Lnk_Navigation_contact-us"
              linkStyle={
                isUIRedesignEnabled
                  ? {
                      ...styles.link,
                      borderBottom:
                        currentItem === 'contact-us'
                          ? `0.125rem solid ${tokens.colors.rsmBlue.primary}`
                          : 'none',
                      paddingY: 0,
                      fontFamily:
                        currentItem === 'contact-us'
                          ? 'Prelo-Bold '
                          : 'Prelo-Book',
                      fontWeight: 400,
                      maxHeight: '1.625rem',
                    }
                  : undefined
              }
              items={dropdownItems}
              menuStyle={
                showWelcome
                  ? undefined
                  : {
                      backgroundColor: tokens.colors.black,
                      border: `0.0625rem solid ${tokens.colors.black}`,
                      '& a': {
                        color: tokens.colors.white,
                        '&:hover': {
                          backgroundColor: tokens.colors.white,
                          color: tokens.colors.black,
                        },
                      },
                    }
              }
            />
          ) : (
            <RouterNavListItem
              to="/contact-us"
              value="contact-us"
              testId="Lnk_Navigation_contact-us"
              linkStyle={isUIRedesignEnabled ? styles.link : undefined}>
              {t('Navigation.ContactUs')}
            </RouterNavListItem>
          )}
        </List>
      </TabContext>
    </Box>
  )
}

export default DesktopMenu
